import {createElement, forwardRef} from 'react';
import {withCssModule} from '@pb/kit-util-with-css-module';
import styles from './pill-base.module.styl';
import type {ComponentPropsWithRef} from 'react';


const classNames = withCssModule(styles);

export interface PillBaseProps extends ComponentPropsWithRef<'button'> {
    /**
     * Element to render as (button or div)
     * @default 'button'
     */
    as?: 'button' | 'div',
}

/**
 * @description The `PillBase` component is a primitive component that provides the core styles for pill-like UI elements.
 *
 * ## Features
 * - Provides core pill styling
 * - Supports both interactive (button) and non-interactive (div) modes
 *
 * ## Accessibility
 * - Keyboard accessible when rendered as a button
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/forms-strip-of-pills-pill-pill-base--documentation}
 */
export const PillBase = forwardRef<HTMLButtonElement | HTMLDivElement, PillBaseProps>(
    function PillBase(props, ref) {
        const {
            as = 'button',
            className,
            children,
            disabled = false,
            type = 'button',
            ...restProps
        } = props;

        const pillBaseClassName = classNames(
            'pill-base', className,
            {
                'pill-base_disabled': disabled,
            },
        );

        return createElement(as, {
            ...(disabled ? {'aria-disabled': true} : {}),
            ...(as === 'button' ? {disabled, type} : {}),
            ...restProps,
            ref,
            className: pillBaseClassName,
        }, children);
    },
);

PillBase.displayName = 'PillBase';
